import { useCallback } from 'react';
import { useNavigate as useNavigateRR } from 'react-router-dom';
import { getB2CLink } from 'tools/getB2CLink/getB2CLink';
import { ROUTES } from '../config';
import { HttpError } from '../types';
import HttpStatusCode from '../types/HttpStatusCode';

export type UseNavigateReturn = {
  navigateBack: () => void;
  navigateToHome: () => void;
  navigateToPageNotFound: () => void;
  navigateToErrorPage: (error: HttpError) => void;
  navigateToDashboard: () => void;
  navigateToReports: () => void;
  navigateToReport: (reportName: string) => void;
  navigateToSummary: (consultationId: number) => void;
  navigateToResponse: (consultationId: number) => void;
  navigateToArchivedResponse: (consultationId: number, responseId: number) => void;
  navigateToDetails: (consultationId: number) => void;
  navigateToManageTeam: () => void;
  navigateToManageAdminTeam: () => void;
  navigateToB2CEditProfile: () => void;
  navigateToB2CChangePassword: () => void;
  navigateToTrafficMonitor: () => void;
  navigateToManageConsultees: () => void;
  navigateToOrganisation: (organisationId: number) => void;
  navigateToTeam: (organisationId: number) => void;
  navigateToTeamUsers: (organisationId: number) => void;
  navigateToCreateOrganisation: () => void;
};

export const useNavigate = (): UseNavigateReturn => {
  const navigate = useNavigateRR();

  const navigateBack = useCallback(() => navigate(-1), [navigate]);
  const navigateToHome = useCallback(() => navigate('/'), [navigate]);
  const navigateToPageNotFound = useCallback(() => navigate(ROUTES.PAGENOTFOUND), [navigate]);
  const navigateToUnauthorised = useCallback(() => navigate(ROUTES.UNAUTHORISED), [navigate]);
  const navigateToDashboard = useCallback(() => navigate(ROUTES.SEARCH), [navigate]);
  const navigateToReports = useCallback(() => navigate(ROUTES.REPORTS), [navigate]);
  const navigateToManageTeam = useCallback(() => navigate(ROUTES.MANAGETEAM), [navigate]);
  const navigateToTrafficMonitor = useCallback(() => navigate(ROUTES.MONITOR), [navigate]);
  const navigateToManageConsultees = useCallback(
    () => navigate(ROUTES.MANAGECONSULTEES),
    [navigate]
  );
  const navigateToCreateOrganisation = useCallback(
    () => navigate(ROUTES.CREATEORGANISATION),
    [navigate]
  );
  const navigateToManageAdminTeam = useCallback(
    () => navigate(ROUTES.MANAGEADMINUSERS),
    [navigate]
  );

  const navigateToReport = (reportName: string) => navigate(`/report/${reportName}`);

  const navigateToSummary = (consultationId: number) =>
    navigate(`/consultation/${consultationId}`);

  const navigateToResponse = (consultationId: number) =>
    navigate(`/consultation/${consultationId}/response`);

  const navigateToArchivedResponse = (consultationId: number, responseId: number) =>
    navigate(`/consultation/${consultationId}/response/${responseId}`);

  const navigateToDetails = (consultationId: number) =>
    navigate(`/consultation/${consultationId}/details`);

  const navigateToB2CEditProfile = () => {
    window.location.href = getB2CLink('editProfile');
  };

  const navigateToB2CChangePassword = () => {
    window.location.href = getB2CLink('passwordReset');
  };

  const navigateToOrganisation = (organisationId: number) => {
    navigate(`/organisations/${organisationId}`);
  };

  const navigateToTeam = (organisationId: number) => {
    navigate(`/organisations/${organisationId}/teams`);
  };

  const navigateToTeamUsers = (organisationId: number) => {
    navigate(`/organisations/${organisationId}/users`);
  };

  const navigateToErrorPage = (error: HttpError) => {
    if (
      error &&
      (error.status === HttpStatusCode.NOT_FOUND ||
        error.status === HttpStatusCode.BAD_REQUEST)
    ) {
      navigateToPageNotFound();
    }
    if (
      (error && error.status === HttpStatusCode.FORBIDDEN) ||
      error.status === HttpStatusCode.UNAUTHORIZED
    ) {
      navigateToUnauthorised();
    }
  };

  return {
    navigateBack,
    navigateToHome,
    navigateToPageNotFound,
    navigateToErrorPage,
    navigateToDashboard,
    navigateToReports,
    navigateToReport,
    navigateToSummary,
    navigateToResponse,
    navigateToArchivedResponse,
    navigateToDetails,
    navigateToManageTeam,
    navigateToManageAdminTeam,
    navigateToB2CEditProfile,
    navigateToB2CChangePassword,
    navigateToTrafficMonitor,
    navigateToManageConsultees,
    navigateToOrganisation,
    navigateToTeam,
    navigateToTeamUsers,
    navigateToCreateOrganisation,
  };
};
